import { inject, Injectable } from "@angular/core";
import { AppState } from '../app.state';
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { DatabaseTagsService } from "src/app/services/database-tags.service";
import { EntityTagActions } from "@states/entity-tag/entity-tag.action-types";
import { exhaustMap, switchMap, share, catchError, withLatestFrom } from "rxjs";
import { ContextualLoggerService } from "src/app/services/contextual-logger.service";
import { LoggerService } from "src/app/services/logger.service";
import { UtilsService } from "src/app/edge/utils.service";

@Injectable()
export class EntityTagEffect {
    private baseLogger = inject(LoggerService);
    private context = 'EntityTagEffect';
    private logger = new ContextualLoggerService(this.baseLogger, this.context);

    public startLoadingEntitiesByTag$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityTagActions.startLoadingEntitiesByTag),
            exhaustMap(({ id, entityTypesFilter }) => [
                EntityTagActions.resetToInitialState(),
                EntityTagActions.setIsLoading({ isLoading: true }),
                EntityTagActions.getEntitiesByTag({ id: id, entityTypesFilter: entityTypesFilter })
            ]),
        ),
    );

    public getEntitiesByTag$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityTagActions.getEntitiesByTag),
            withLatestFrom(this.store$.pipe(select(state => state.entityTagState))),
            switchMap(([{ id, entityTypesFilter }, { perPage, isLastPage, lastCreatedAt }]) => {
                if (!isLastPage) {
                    return this.databaseTagsService.getEntitiesByTag(perPage, lastCreatedAt, id, entityTypesFilter)
                        .pipe(
                            switchMap(entitiesByTag => {
                                return [
                                    EntityTagActions.getEntitiesByTagSuccess({ entitiesByTag: entitiesByTag }),
                                    EntityTagActions.setIsLoading({ isLoading: false }),
                                ];
                            }),
                            catchError(response => {
                                const errorMessage = this.utilsService.errMessage(response);
                                this.logger.error(errorMessage);
                                return [
                                    EntityTagActions.setIsLoading({ isLoading: false }),
                                ];
                            }),
                        );
                } else {
                    return [
                        EntityTagActions.setIsLoading({ isLoading: false })
                    ];
                }
            },
            ),
            share(),
        ),
    );

    public setEntityTypesFilter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EntityTagActions.startSettingEntityTypesFilter),
            switchMap(({ id, entityTypesFilter }) => [
                EntityTagActions.startLoadingEntitiesByTag({ id: id, entityTypesFilter: entityTypesFilter })
            ]),
        ),
    );

    constructor(
        private actions$: Actions,
        private store$: Store<AppState>,
        private databaseTagsService: DatabaseTagsService,
        private utilsService: UtilsService,
    ) { }
}